import * as React from 'react'
import { matchPath, NavLink, useLocation } from 'react-router-dom'
import { CircleHelp, Search } from 'lucide-react'
import { UserButton, useUser, OrganizationSwitcher } from '@clerk/clerk-react'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  RAIL_BUTTON_ACTIVE_CLASS,
  RAIL_BUTTON_CLASS,
  useSidebar,
} from '@/components/ui/sidebar'
import { AccessLock } from '@/components/AccessLock'
import { AppSidebarFlyout } from '@/components/AppSidebarFlyout'
import { useAppSidebarNav, type NavCategory } from '@/components/AppSidebarNav'
import careforceLogo from '@/images/brand/careforce-logo-with-title.png'
import { SIDEBAR_FLYOUT_CLOSE_DELAY_MS } from '@/config/constants'
import { cn } from '@/lib/utils'
import { useCapability } from '@/features/permissions/hooks'
import { useAtlasChatOpen } from '@/hooks/useAtlasChatOpen'
import { useCommandPaletteStore } from '@/stores/commandPaletteStore'
import { NotificationBell } from '@/features/notifications/components/NotificationBell'

// NavLink applies `.active` (and `aria-current="page"`) to the underlying
// <a> itself; rows draw the same highlight both places rather than relying
// on an ambient container-level rule.
const NAV_LINK_ACTIVE_CLASS = cn(
  '[&.active]:bg-primary-active-bg [&[aria-current=page]]:bg-primary-active-bg',
  '[&.active_svg]:text-primary-solid [&[aria-current=page]_svg]:text-primary-solid'
)

export type AppSidebarHandle = {
  openDrawer: () => void
  closeDrawer: () => void
}

interface AppSidebarProps {
  ref?: React.Ref<AppSidebarHandle>
}

function SidebarSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-2 mt-3 mb-1 select-none whitespace-nowrap">
      <span className="block text-[11px] text-primary-high-contrast/50">
        {children}
      </span>
    </div>
  )
}

/**
 * The Atlas support-chat toggle, shared by the rail (icon only) and the
 * mobile drawer (icon + label). data-atlas-chat-trigger keeps
 * useAtlasChatDismiss from treating this control as an outside click, which
 * would close the chat on pointerdown only for this onClick to reopen it.
 */
function SupportAction({
  variant,
  atlasChatOpen,
}: {
  variant: 'rail' | 'menu'
  atlasChatOpen: boolean
}) {
  const label = atlasChatOpen ? 'Close support' : 'Support'

  const handleClick = React.useCallback(() => {
    window.Atlas?.call?.(atlasChatOpen ? 'closeChatWindow' : 'openChatWindow')
  }, [atlasChatOpen])

  if (variant === 'rail') {
    return (
      <button
        type="button"
        aria-label={label}
        data-atlas-chat-trigger=""
        className={RAIL_BUTTON_CLASS}
        onClick={handleClick}>
        <CircleHelp size={22} strokeWidth={1.2} />
      </button>
    )
  }

  return (
    <SidebarMenuButton asChild>
      <button type="button" data-atlas-chat-trigger="" onClick={handleClick}>
        <CircleHelp size={16} />
        <span className="text-xs">{label}</span>
      </button>
    </SidebarMenuButton>
  )
}

// Memoized: AuthLayout re-renders on every frame of a chat-sidebar drag (it
// owns --chat-width), and the one prop it passes is a stable useRef object —
// so this is an unconditional bail-out for the whole left rail.
export const AppSidebar = React.memo(function AppSidebar({
  ref,
}: AppSidebarProps) {
  return (
    <SidebarProvider>
      <AppSidebarInner ref={ref} />
    </SidebarProvider>
  )
})

function AppSidebarInner({ ref }: { ref?: React.Ref<AppSidebarHandle> }) {
  const { openMobile, isMobile, setOpenMobile } = useSidebar()
  const mobileOpen = isMobile && openMobile
  const { user } = useUser()
  const atlasChatOpen = useAtlasChatOpen()
  const showOrgSwitcher = useCapability('orgSwitcher.switch') === true
  const categories = useAppSidebarNav()
  const displayName =
    user?.fullName ||
    user?.primaryEmailAddress?.emailAddress ||
    user?.username ||
    'Account'

  React.useImperativeHandle(
    ref,
    () => ({
      openDrawer: () => setOpenMobile(true),
      closeDrawer: () => setOpenMobile(false),
    }),
    [setOpenMobile]
  )

  // Close the mobile drawer whenever a nav link inside it is clicked.
  const handleNavContainerClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const target = event.target as HTMLElement | null
      if (!target?.closest('a')) return
      if (isMobile && openMobile) setOpenMobile(false)
    },
    [isMobile, openMobile, setOpenMobile]
  )

  return (
    <>
      <Sidebar
        className={cn(
          // Mobile drawer (existing positioning preserved).
          isMobile && !openMobile && 'hidden',
          isMobile &&
            openMobile &&
            // h-auto — the Sidebar primitive's `h-full` would otherwise beat
            // the bottom inset and run the footer under the nav bar.
            'flex fixed top-0 bottom-[calc(var(--bottom-nav-height)+env(safe-area-inset-bottom))] left-0 z-40 h-auto w-[85vw] max-w-[320px] bg-app shadow-md',
          // Desktop: a permanent icon rail, absolutely positioned over the
          // shell grid's first track, so category flyouts can render on
          // top of content without reflowing it.
          'md:absolute md:inset-y-0 md:left-0 md:z-40 md:flex md:flex-col md:items-center',
          'md:w-[var(--app-sidebar-width)] px-2 pt-3 pb-6 md:bg-transparent',
          // Short viewports (or heavy zoom) leave less height than the rail's
          // fixed-size controls need. Without this the flex column compresses
          // their 40px hit areas first and then pushes the footer controls off
          // screen with no way to reach them; `scrollbar-hidden` keeps the
          // 56px rail from widening for a scrollbar.
          'md:overflow-y-auto md:overflow-x-hidden scrollbar-hidden',
          // The drawer's rows inherit their colour from here, as they did
          // before the redesign. The rail's own controls carry it on
          // RAIL_BUTTON_CLASS, so these rules only reach the drawer in
          // practice — dropping them left phone labels in the primitive's
          // faded body grey.
          '[&_a]:text-primary-high-contrast [&_button]:text-primary-high-contrast'
        )}>
        {mobileOpen ? (
          <MobileNav
            categories={categories}
            onNavContainerClick={handleNavContainerClick}
            atlasChatOpen={atlasChatOpen}
            showOrgSwitcher={showOrgSwitcher}
            displayName={displayName}
          />
        ) : (
          <DesktopRail
            categories={categories}
            atlasChatOpen={atlasChatOpen}
            showOrgSwitcher={showOrgSwitcher}
          />
        )}
      </Sidebar>

      {/* Mobile drawer backdrop — was MobileSidebarOverlay in AuthLayout.
          position:fixed positions relative to viewport, not ancestor; safe to nest. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 md:hidden z-30"
          onClick={() => setOpenMobile(false)}
          aria-hidden="true"
        />
      )}
    </>
  )
}

function DesktopRail({
  categories,
  atlasChatOpen,
  showOrgSwitcher,
}: {
  categories: NavCategory[]
  atlasChatOpen: boolean
  showOrgSwitcher: boolean
}) {
  const [openCategoryId, setOpenCategoryId] = React.useState<string | null>(
    null
  )
  const closeTimerRef = React.useRef<number | undefined>(undefined)
  const railButtonRefs = React.useRef(new Map<string, HTMLButtonElement>())
  // Wraps the rail buttons AND the (non-portaled) flyout, so "outside" this
  // element genuinely means outside both — see the effect below.
  const containerRef = React.useRef<HTMLDivElement>(null)
  // The open flyout's panel. Dismissal containment keys on this rather than
  // the rail as a whole — see onFocusOut below.
  const flyoutPanelRef = React.useRef<HTMLDivElement>(null)

  const clearCloseTimer = React.useCallback(() => {
    if (closeTimerRef.current !== undefined) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = undefined
    }
  }, [])

  // Hover opens the flyout without disturbing focus; click and Enter/Space
  // (which the browser delivers as a click) hand focus to the first row, so a
  // keyboard user lands on the links instead of tabbing the rest of the rail
  // first — the flyout renders after all five category buttons.
  const [focusFirstRow, setFocusFirstRow] = React.useState(false)

  const openCategory = React.useCallback(
    (id: string, moveFocus = false) => {
      clearCloseTimer()
      setOpenCategoryId(id)
      setFocusFirstRow(moveFocus)
    },
    [clearCloseTimer]
  )

  const scheduleClose = React.useCallback(() => {
    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(() => {
      setOpenCategoryId(null)
    }, SIDEBAR_FLYOUT_CLOSE_DELAY_MS)
  }, [clearCloseTimer])

  // `restoreFocus` sends focus back to the category button — right for Escape
  // and for a click that toggles the flyout shut, wrong for a hover-out or an
  // outside click, which must leave focus wherever the user put it.
  const closeImmediate = React.useCallback(
    (restoreFocus = false) => {
      clearCloseTimer()
      if (restoreFocus && openCategoryId) {
        railButtonRefs.current.get(openCategoryId)?.focus()
      }
      setOpenCategoryId(null)
      setFocusFirstRow(false)
    },
    [clearCloseTimer, openCategoryId]
  )

  React.useEffect(() => clearCloseTimer, [clearCloseTimer])

  // Escape, an outside pointerdown, or focus leaving the flyout and its own
  // category button close it immediately.
  React.useEffect(() => {
    if (!openCategoryId) return

    // The open flyout's own territory: the panel and the button that opened
    // it. Deliberately narrower than containerRef, which also wraps the
    // search button, the other categories and the footer controls — treating
    // those as "inside" left the flyout open behind a user who had tabbed
    // onto the bell, with aria-expanded stuck true. One definition, so the
    // dismissal and focus-restore rules cannot drift apart.
    const isInsideFlyout = (node: Node | null) => {
      if (!node) return false
      const anchor = railButtonRefs.current.get(openCategoryId)
      return Boolean(
        flyoutPanelRef.current?.contains(node) || anchor?.contains(node)
      )
    }

    const onKeyDown = (event: KeyboardEvent) => {
      // Restore focus only when it is in the flyout to begin with. Dismissing
      // a hover-opened panel must not move the caret of someone typing
      // elsewhere — or pull focus off Support, the bell or another category.
      if (event.key === 'Escape') {
        closeImmediate(isInsideFlyout(document.activeElement))
      }
    }
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return
      closeImmediate()
    }
    const onFocusOut = (event: FocusEvent) => {
      if (isInsideFlyout(event.relatedTarget as Node | null)) return
      closeImmediate()
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    const container = containerRef.current
    container?.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
      container?.removeEventListener('focusout', onFocusOut)
    }
  }, [openCategoryId, closeImmediate])

  const activeCategory = categories.find((c) => c.id === openCategoryId) ?? null
  const flyoutId = React.useId()

  // Which category owns the page you are on. Without this the rail says
  // nothing about where you are: with every flyout closed, the only
  // active-route styling lives on rows that aren't rendered, so on /calls all
  // five icons look identical. Items carry the current query string in `to`,
  // so match on the pathname alone.
  const { pathname } = useLocation()
  const currentRouteCategoryId = React.useMemo(
    () =>
      categories.find((category) =>
        category.items.some(
          (item) =>
            !item.external &&
            matchPath(
              {
                path: item.to.split('?')[0] ?? item.to,
                end: item.end ?? false,
              },
              pathname
            ) !== null
        )
      )?.id ?? null,
    [categories, pathname]
  )

  return (
    <div ref={containerRef} className="contents">
      <div className="flex size-10 shrink-0 items-center justify-center">
        <div className="w-[22px] overflow-hidden">
          <img
            src={careforceLogo}
            alt="Careforce"
            className="h-4 w-auto max-w-none select-none"
            draggable={false}
          />
        </div>
      </div>

      <button
        type="button"
        aria-label="Search"
        className={cn(RAIL_BUTTON_CLASS, 'mt-8')}
        onClick={() => useCommandPaletteStore.getState().openPalette()}>
        <Search size={22} strokeWidth={1.2} />
      </button>

      <div className="mt-8 flex shrink-0 flex-col gap-4">
        {categories.map((category) => {
          const Icon = category.icon
          const isOpen = category.id === openCategoryId
          return (
            <div
              key={category.id}
              className="flex w-10 shrink-0 flex-col items-center gap-0.5">
              <button
                type="button"
                ref={(node) => {
                  if (node) railButtonRefs.current.set(category.id, node)
                  else railButtonRefs.current.delete(category.id)
                }}
                // Not `aria-haspopup="menu"`: the flyout is a <nav> of links,
                // and promising a menu makes screen readers advertise
                // arrow-key navigation this doesn't implement.
                aria-expanded={isOpen}
                aria-controls={isOpen ? flyoutId : undefined}
                aria-label={category.label}
                // The rail's only cue for the current section used to be a
                // colour change. The active row now lives inside a flyout
                // that isn't rendered when closed, so screen readers had
                // nothing to announce.
                aria-current={
                  category.id === currentRouteCategoryId ? 'true' : undefined
                }
                className={cn(
                  RAIL_BUTTON_CLASS,
                  category.id === currentRouteCategoryId &&
                    RAIL_BUTTON_ACTIVE_CLASS
                )}
                onMouseEnter={() => openCategory(category.id)}
                onMouseLeave={scheduleClose}
                // A click toggles shut only when a click or key opened this
                // flyout. Hovering already opened it, so a blind toggle would
                // shut it under the pointer that was committing to it — and
                // `mouseenter` does not fire again, leaving it shut until the
                // pointer leaves and returns. `focusFirstRow` is the record of
                // how it was opened, so a click on a hovered flyout promotes
                // it instead, handing over focus.
                onClick={() =>
                  isOpen && focusFirstRow
                    ? closeImmediate(true)
                    : openCategory(category.id, true)
                }>
                <Icon size={22} strokeWidth={1.2} />
              </button>
              <span className="w-full text-center text-[10px] font-medium leading-normal text-primary-high-contrast">
                {category.label}
              </span>
            </div>
          )
        })}
      </div>

      {activeCategory && (
        <AppSidebarFlyout
          id={flyoutId}
          panelRef={flyoutPanelRef}
          category={activeCategory}
          anchorEl={railButtonRefs.current.get(activeCategory.id) ?? null}
          focusFirstRow={focusFirstRow}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleClose}
          onNavigate={() => closeImmediate()}
        />
      )}

      <div className="min-h-8 flex-1" />

      <div className="flex shrink-0 flex-col items-center gap-1">
        <NotificationBell variant="rail" />
        <SupportAction variant="rail" atlasChatOpen={atlasChatOpen} />
      </div>

      <div className="mt-8 flex shrink-0 flex-col items-center gap-2">
        {showOrgSwitcher && (
          <div className="flex size-8 items-center justify-center rounded-lg border border-neutral-ui-border bg-app">
            <OrganizationSwitcher
              appearance={{
                elements: {
                  // Keep rail-only styling off Clerk's portaled dropdown rows.
                  organizationPreviewTextContainer:
                    '[.cl-organizationSwitcherTrigger_&]:!hidden',
                  organizationSwitcherTriggerIcon: '!hidden',
                  organizationPreviewAvatarBox:
                    '[.cl-organizationSwitcherTrigger_&]:!size-6',
                },
              }}
            />
          </div>
        )}
        <UserButton
          appearance={{ elements: { avatarBox: '!size-8' } }}
          showName={false}
          userProfileMode="navigation"
          userProfileUrl="/user-profile"
        />
      </div>
    </div>
  )
}

function MobileNav({
  categories,
  onNavContainerClick,
  atlasChatOpen,
  showOrgSwitcher,
  displayName,
}: {
  categories: NavCategory[]
  onNavContainerClick: (event: React.MouseEvent<HTMLElement>) => void
  atlasChatOpen: boolean
  showOrgSwitcher: boolean
  displayName: string
}) {
  const userButtonRef = React.useRef<HTMLDivElement>(null)

  return (
    <>
      <SidebarHeader className="pl-2 pt-2 mb-6">
        <div className="w-[106px]">
          <img
            src={careforceLogo}
            alt="Careforce"
            className="h-4 w-auto max-w-none select-none"
            draggable={false}
          />
        </div>
      </SidebarHeader>

      <SidebarContent className="scrollbar-hidden overflow-x-hidden">
        <SidebarMenu onClick={onNavContainerClick}>
          <SidebarMenuItem>
            {categories.map((category) => (
              <React.Fragment key={category.id}>
                <SidebarSectionLabel>{category.label}</SidebarSectionLabel>
                {category.items.map((item) => {
                  const Icon = item.icon
                  return (
                    <SidebarMenuButton asChild key={item.id}>
                      {item.external ? (
                        // Never "active" — a plain <a> to an external URL,
                        // not a NavLink — so it skips the active-state class.
                        <a href={item.to}>
                          <Icon size={16} />
                          <span className="text-xs">{item.label}</span>
                          {item.locked && <AccessLock className="ml-auto" />}
                        </a>
                      ) : (
                        <NavLink
                          to={item.to}
                          end={item.end}
                          className={NAV_LINK_ACTIVE_CLASS}>
                          <Icon size={16} />
                          <span className="text-xs">{item.label}</span>
                          {item.locked && <AccessLock className="ml-auto" />}
                        </NavLink>
                      )}
                    </SidebarMenuButton>
                  )
                })}
              </React.Fragment>
            ))}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <NotificationBell open />
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SupportAction variant="menu" atlasChatOpen={atlasChatOpen} />
          </SidebarMenuItem>
          {showOrgSwitcher && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <OrganizationSwitcher
                  appearance={{
                    elements: {
                      organizationPreviewTextContainer:
                        '[.sidebar-menu-item_&]:!block',
                      organizationSwitcherTriggerIcon:
                        '[.sidebar-menu-item_&]:!block',
                      organizationPreviewAvatarBox:
                        '[.sidebar-menu-item_&]:!w-4 [.sidebar-menu-item_&]:!h-4',
                    },
                  }}
                />
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <div className="flex items-center gap-3 cursor-pointer">
                <div ref={userButtonRef}>
                  <UserButton
                    appearance={{
                      elements: {
                        avatarBox:
                          '[.sidebar-menu-item_&]:!w-4 [.sidebar-menu-item_&]:!h-4',
                      },
                    }}
                    showName={false}
                    userProfileMode="navigation"
                    userProfileUrl="/user-profile"
                  />
                </div>
                <span
                  className="text-xs"
                  onClick={(e) => {
                    e.preventDefault()
                    userButtonRef.current?.querySelector('button')?.click()
                  }}>
                  {displayName}
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </>
  )
}
